// ─────────────────────────────────────────────────────────────────────────
// FO-149 (batch-29) — beschikbaarheid per uitrusting-artikel (doelgroepen).
//
// Per artikel is instelbaar voor wie het beschikbaar is: alle medewerkers
// (default, geen regels) of beperkt tot functietypes / afdelingen / BV's /
// specifieke medewerkers. OR-semantiek over de regels (zelfde semantiek als
// de gereedschapspakketten, FO-041). Het gedeelde Zod-schema wordt door de
// API (`.safeParse`) én het backoffice-form (client-side) gebruikt zodat het
// form niets kan sturen dat de API weigert.
// ─────────────────────────────────────────────────────────────────────────

import { z } from 'zod'

/** Functietypes (Prisma `EmployeeType`) waarop een doelgroep-regel kan matchen. */
export const UITRUSTING_DOELGROEP_EMPLOYEE_TYPES = [
  'ONDERHOUD',
  'STORING',
  'SOFTWARE',
  'PROJECT',
  'UNIVERSEEL',
  'KANTOOR',
] as const

export type UitrustingDoelgroepEmployeeType =
  (typeof UITRUSTING_DOELGROEP_EMPLOYEE_TYPES)[number]

/** NL-labels voor de functietype-checkboxes ("Storing/service" dekt de servicedienst). */
export const UITRUSTING_DOELGROEP_EMPLOYEE_TYPE_LABELS: Record<
  UitrustingDoelgroepEmployeeType,
  string
> = {
  ONDERHOUD: 'Onderhoud',
  STORING: 'Storing/service',
  SOFTWARE: 'Software',
  PROJECT: 'Project',
  UNIVERSEEL: 'Universeel',
  KANTOOR: 'Kantoor',
}

// ─── DTO's ──────────────────────────────────────────────────────────────────

/**
 * Eén doelgroep-regel op een artikel (read-DTO, beheer-endpoint). Precies één
 * van de soorten is gevuld; de bijbehorende naam wordt meegeleverd voor
 * de badge/lijst-weergave.
 */
export interface UitrustingArtikelDoelgroepRegelDTO {
  id: string
  employeeType: UitrustingDoelgroepEmployeeType | null
  departmentId: string | null
  departmentNaam: string | null
  entityId: string | null
  entityNaam: string | null
  employeeId: string | null
  employeeNaam: string | null
  /**
   * batch-41 (FO-359) — BEZIT-criterium: de medewerker matcht met een
   * uitgegeven, niet-geretourneerd exemplaar van dit drager-template
   * (`dragerTemplateId`, scherp) of waarvan merk/model/artikelnaam/dragernaam
   * de merk-tekst bevat (`bezitMerk`, fallback).
   */
  dragerTemplateId: string | null
  dragerTemplateNaam: string | null
  bezitMerk: string | null
  /** False wanneer een medewerker-regel wijst naar een uit-dienst/verwijderde medewerker, of een BEZIT-regel naar een verwijderd template (matcht dan niet meer). */
  actief: boolean
}

/**
 * Afgeleid doelgroep-blok op de artikel-DTO (alleen in de beheer-respons; de
 * portal krijgt dit blok niet — voor de monteur bestaat een niet-beschikbaar
 * artikel simpelweg niet).
 */
export interface UitrustingArtikelDoelgroepDTO {
  /** True zodra het artikel actieve doelgroep-regels heeft (geen regels = iedereen). */
  beperkt: boolean
  regels: UitrustingArtikelDoelgroepRegelDTO[]
  /**
   * True wanneer het artikel beperkt is maar geen enkele regel meer iemand kán
   * matchen (bv. alle individuele medewerkers uit dienst) — het artikel is dan
   * feitelijk onzichtbaar in de portal; de catalogus-lijst waarschuwt.
   */
  effectiefLeeg: boolean
}

// ─── Gedeeld schrijf-schema (API + backoffice-form) ─────────────────────────

/**
 * Eén doelgroep-regel in het schrijfpad. Precies één soort moet gevuld zijn
 * (functietype, afdeling, BV, medewerker, of — batch-41 FO-359 — het
 * BEZIT-criterium via drager-template of merk-tekst) — de OR-semantiek zit
 * tussen de regels, niet binnen een regel.
 */
export const UitrustingDoelgroepRegelInputSchema = z
  .object({
    employeeType: z.enum(UITRUSTING_DOELGROEP_EMPLOYEE_TYPES).nullable().optional(),
    departmentId: z.string().min(1).nullable().optional(),
    entityId: z.string().min(1).nullable().optional(),
    employeeId: z.string().min(1).nullable().optional(),
    // batch-41 (FO-359) — BEZIT: template-verwijzing (scherp) of merk-tekst
    // (fallback; getrimd, minimaal 2 tekens tegen per-ongeluk-alles-matchen).
    dragerTemplateId: z.string().min(1).nullable().optional(),
    bezitMerk: z.string().trim().min(2).max(120).nullable().optional(),
  })
  .superRefine((val, ctx) => {
    const gevuld = [
      val.employeeType,
      val.departmentId,
      val.entityId,
      val.employeeId,
      val.dragerTemplateId,
      val.bezitMerk,
    ].filter((v) => v != null).length
    if (gevuld !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          'Elke doelgroep-regel heeft precies één soort: functietype, afdeling, BV, medewerker of bezit',
      })
    }
  })

export type UitrustingDoelgroepRegelInput = z.infer<typeof UitrustingDoelgroepRegelInputSchema>

/**
 * Het `doelgroep`-veld op artikel-POST/PATCH (vervang-semantiek): de meegegeven
 * regels vervangen de bestaande set. Lege array = beperking opheffen (iedereen);
 * weggelaten (undefined) = doelgroep ongemoeid laten (PATCH) of iedereen (POST).
 */
export const UitrustingDoelgroepInputSchema = z
  .array(UitrustingDoelgroepRegelInputSchema)
  .max(200, 'Maximaal 200 doelgroep-regels per artikel')

export type UitrustingDoelgroepInput = z.infer<typeof UitrustingDoelgroepInputSchema>

// ─── Weergave-helper ─────────────────────────────────────────────────────────

/**
 * Compacte badge-tekst voor de catalogus-lijst: "Iedereen", anders een
 * samenvatting zoals "Onderhoud", "Onderhoud + Storing/service",
 * "3 medewerkers" of "Onderhoud + 2 meer". Puur presentationeel; gedeeld
 * zodat lijst en formulier dezelfde samenvatting tonen.
 */
export function doelgroepBadgeTekst(doelgroep: UitrustingArtikelDoelgroepDTO | null | undefined): string {
  if (!doelgroep || !doelgroep.beperkt) return 'Iedereen'
  const delen: string[] = []
  const types = doelgroep.regels.filter((r) => r.employeeType != null)
  for (const t of types) {
    if (t.employeeType) delen.push(UITRUSTING_DOELGROEP_EMPLOYEE_TYPE_LABELS[t.employeeType])
  }
  for (const r of doelgroep.regels) {
    if (r.departmentId) delen.push(r.departmentNaam ?? 'Afdeling')
    if (r.entityId) delen.push(r.entityNaam ?? 'BV')
    // batch-41 (FO-359) — BEZIT-regels: "Bezit: <template of merk>".
    if (r.dragerTemplateId) delen.push(`Bezit: ${r.dragerTemplateNaam ?? 'drager'}`)
    if (r.bezitMerk) delen.push(`Bezit: ${r.bezitMerk}`)
  }
  const medewerkers = doelgroep.regels.filter((r) => r.employeeId != null)
  if (medewerkers.length === 1) {
    delen.push(medewerkers[0].employeeNaam ?? '1 medewerker')
  } else if (medewerkers.length > 1) {
    delen.push(`${medewerkers.length} medewerkers`)
  }
  if (delen.length === 0) return 'Beperkt'
  if (delen.length <= 2) return delen.join(' + ')
  return `${delen[0]} + ${delen.length - 1} meer`
}
